import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import {
  fetchCareerCoaches,
  fetchCareerCoachesPublic,
  createCareerCoach,
  createCareerCoachPublic
} from '../utils/careerCoachesApi';
import { showError, showSuccess } from './CustomAlert';

const FieldWrap = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${(p) => (p.$hasError ? '#dc2626' : '#e1e5e9')};
  border-radius: 8px;
  font-size: 16px;
  box-sizing: border-box;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);
  }

  &:disabled {
    background: #f8f9fa;
    cursor: not-allowed;
  }
`;

const Dropdown = styled.ul`
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  margin: 0;
  padding: 0;
  list-style: none;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  max-height: 220px;
  overflow-y: auto;
  z-index: 50;
`;

const DropdownItem = styled.li`
  padding: 10px 14px;
  cursor: pointer;
  font-size: 0.95rem;
  color: #2c3e50;

  &:hover,
  &[data-active='true'] {
    background: #f0f4ff;
  }
`;

const DropdownEmpty = styled.li`
  padding: 12px 14px;
  color: #6c757d;
  font-size: 0.9rem;
`;

const AddNewRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;
`;

const SecondaryButton = styled.button`
  background: white;
  color: rgb(51, 66, 147);
  border: 1px solid rgb(51, 66, 147);
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f0f4ff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const NewCoachPanel = styled.div`
  margin-top: 12px;
  padding: 14px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const NewCoachActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;
`;

const PrimarySmallButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const MutedButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
`;

const ErrorText = styled.span`
  color: #dc2626;
  font-size: 0.8rem;
  margin-top: 6px;
  display: block;
`;

const LoadingText = styled.div`
  color: #6c757d;
  font-style: italic;
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  background: #f8f9fa;
`;

function isInvalidCareerCoachName(name) {
  const t = String(name || '').trim().toLowerCase();
  return !t || t === 'n/a' || t === 'none';
}

/**
 * Searchable career coach picker with optional inline create.
 * @param {string} value - Selected coach name (workForceCenter)
 * @param {function} onChange - (name: string) => void
 * @param {boolean} publicMode - Use unauthenticated API (registration form)
 * @param {boolean} canCreate - Show add-new flow (default true in admin, true in public)
 */
export default function CareerCoachField({
  value = '',
  onChange,
  error,
  required = false,
  publicMode = false,
  canCreate = true,
  disabled = false,
  placeholder = 'Search or select career coach...'
}) {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [showNewPanel, setShowNewPanel] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingNew, setSavingNew] = useState(false);
  const wrapRef = useRef(null);

  const loadCoaches = async () => {
    try {
      setLoading(true);
      const list = publicMode
        ? await fetchCareerCoachesPublic()
        : await fetchCareerCoaches({ activeOnly: true });
      setCoaches(list);
    } catch (err) {
      setCoaches([]);
      showError(err.message || 'Failed to load career coaches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCoaches();
  }, [publicMode]);

  useEffect(() => {
    setSearch(value || '');
  }, [value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return coaches;
    return coaches.filter((c) => c.name?.toLowerCase().includes(q));
  }, [coaches, search]);

  const selectCoach = (coach) => {
    onChange(coach.name);
    setSearch(coach.name);
    setOpen(false);
    setShowNewPanel(false);
  };

  const handleSearchChange = (e) => {
    const next = e.target.value;
    setSearch(next);
    setOpen(true);
    setHighlightIndex(0);
    onChange('');
  };

  const handleSearchKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, Math.max(0, filtered.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && open && filtered.length) {
      e.preventDefault();
      selectCoach(filtered[highlightIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleCreateNew = async () => {
    const trimmed = newName.trim();
    if (isInvalidCareerCoachName(trimmed)) {
      showError('Please enter a valid career coach name');
      return;
    }
    try {
      setSavingNew(true);
      const result = publicMode
        ? await createCareerCoachPublic(trimmed)
        : await createCareerCoach({ name: trimmed, isActive: true });
      const coachName = result.name || result.careerCoach?.name || trimmed;
      await loadCoaches();
      onChange(coachName);
      setSearch(coachName);
      setShowNewPanel(false);
      setNewName('');
      showSuccess(result.existing ? 'Career coach selected' : 'Career coach created');
    } catch (err) {
      showError(err.message || 'Failed to create career coach');
    } finally {
      setSavingNew(false);
    }
  };

  if (loading) {
    return <LoadingText>Loading career coaches...</LoadingText>;
  }

  return (
    <div>
      <FieldWrap ref={wrapRef}>
        <SearchInput
          type="text"
          value={search}
          onChange={handleSearchChange}
          onFocus={() => setOpen(true)}
          onKeyDown={handleSearchKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          disabled={disabled || showNewPanel}
          $hasError={!!error}
          required={required && !value}
          aria-autocomplete="list"
          aria-expanded={open}
        />
        {open && !showNewPanel && (
          <Dropdown role="listbox">
            {filtered.length === 0 ? (
              <DropdownEmpty>No matches. Add a new career coach below.</DropdownEmpty>
            ) : (
              filtered.map((coach, idx) => (
                <DropdownItem
                  key={coach.id}
                  role="option"
                  data-active={idx === highlightIndex}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectCoach(coach);
                  }}
                  onMouseEnter={() => setHighlightIndex(idx)}
                >
                  {coach.name}
                </DropdownItem>
              ))
            )}
          </Dropdown>
        )}
      </FieldWrap>

      {canCreate && !showNewPanel && (
        <AddNewRow>
          <SecondaryButton
            type="button"
            onClick={() => {
              setShowNewPanel(true);
              setNewName(search.trim());
              setOpen(false);
            }}
            disabled={disabled}
          >
            + Add new career coach
          </SecondaryButton>
        </AddNewRow>
      )}

      {canCreate && showNewPanel && (
        <NewCoachPanel>
          <SearchInput
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New career coach name"
            disabled={savingNew}
            $hasError={false}
          />
          <NewCoachActions>
            <PrimarySmallButton type="button" onClick={handleCreateNew} disabled={savingNew}>
              {savingNew ? 'Saving...' : 'Save career coach'}
            </PrimarySmallButton>
            <MutedButton
              type="button"
              onClick={() => {
                setShowNewPanel(false);
                setNewName('');
              }}
              disabled={savingNew}
            >
              Cancel
            </MutedButton>
          </NewCoachActions>
        </NewCoachPanel>
      )}

      {error && <ErrorText>{error}</ErrorText>}
    </div>
  );
}
